import type {
  Balance,
  Employee,
  EmployeeCreate,
  EmployeeDirectoryStats,
  EmployeeUpdate,
  EmploymentType,
  EmploymentTypeUpdateRequest,
  EmploymentTypeUpdateResponse,
  Leave,
  LeaveStatus,
  Race,
  Religion,
  Role,
} from '@lms/shared-types'
import { apiRequest, apiRequestPaged, type PagedResult } from './client'

export interface ListEmployeesQuery {
  team?: string
  role?: Role
  active?: boolean
  search?: string
  /** Directory quick-filter: only this employment type (e.g. `contractual`). */
  employmentType?: EmploymentType
  /** Directory quick-filter: only employees on / off probation. */
  probation?: boolean
  /** Directory quick-filter: only employees whose join-date month == this (1–12). */
  anniversaryMonth?: number
  page?: number
  limit?: number
}

function buildEmployeesQuery(query: ListEmployeesQuery): string {
  const params = new URLSearchParams()
  if (query.team) params.set('team', query.team)
  if (query.role) params.set('role', query.role)
  if (query.active !== undefined) params.set('active', String(query.active))
  if (query.search) params.set('search', query.search)
  if (query.employmentType) params.set('employment_type', query.employmentType)
  if (query.probation !== undefined) params.set('probation', String(query.probation))
  if (query.anniversaryMonth != null)
    params.set('anniversary_month', String(query.anniversaryMonth))
  if (query.page != null) params.set('page', String(query.page))
  if (query.limit != null) params.set('limit', String(query.limit))
  return params.toString()
}

/** Fetch one page + meta. Use with `useInfiniteQuery` to drive the
 *  virtualized HR Employees list — the page returns `{data, meta}`
 *  so the caller knows whether to call `fetchNextPage()`. */
export function listEmployeesPage(
  query: ListEmployeesQuery = {},
): Promise<PagedResult<Employee>> {
  const qs = buildEmployeesQuery(query)
  return apiRequestPaged<Employee>(`/api/v1/employees${qs ? `?${qs}` : ''}`)
}

/** At-a-glance counts for the HR Employees directory filter cards
 *  (Contractual / Probationers / Anniversary this month). Counts are over
 *  active, non-system employees. HR+ only. */
export function getEmployeeDirectoryStats(): Promise<EmployeeDirectoryStats> {
  return apiRequest<EmployeeDirectoryStats>('/api/v1/employees/directory-stats')
}

export function getEmployee(id: string): Promise<Employee> {
  return apiRequest<Employee>(`/api/v1/employees/${encodeURIComponent(id)}`)
}

export function createEmployee(input: EmployeeCreate): Promise<Employee> {
  return apiRequest<Employee>('/api/v1/employees', {
    method: 'POST',
    body: input,
  })
}

export function updateEmployee(id: string, input: EmployeeUpdate): Promise<Employee> {
  return apiRequest<Employee>(`/api/v1/employees/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: input,
  })
}

/** Move an employee to a different team (structural only — does not touch
 *  approver routing). HR+ only; a 4xx from the API bubbles up as ApiError. */
export function setEmployeeTeam(id: string, teamId: string): Promise<Employee> {
  return updateEmployee(id, { teamId })
}

export function deactivateEmployee(id: string): Promise<Employee> {
  return apiRequest<Employee>(`/api/v1/employees/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
}

export function updateEmploymentType(
  id: string,
  input: EmploymentTypeUpdateRequest,
): Promise<EmploymentTypeUpdateResponse> {
  return apiRequest<EmploymentTypeUpdateResponse>(
    `/api/v1/employees/${encodeURIComponent(id)}/employment-type`,
    { method: 'PATCH', body: input },
  )
}

export function updateReligion(id: string, religion: Religion | null): Promise<Employee> {
  return updateEmployee(id, { religion })
}

export function updateRace(id: string, race: Race | null): Promise<Employee> {
  return updateEmployee(id, { race })
}

/** List balances for a specific employee. Self-read is always allowed;
 *  cross-employee reads require HR+. Used by the "Viewing as" surface
 *  so HR can render an impersonated employee's actual balance card. */
export function listEmployeeBalances(id: string, year?: number): Promise<Balance[]> {
  const qs = year != null ? `?year=${year}` : ''
  return apiRequest<Balance[]>(
    `/api/v1/employees/${encodeURIComponent(id)}/balances${qs}`,
  )
}

/** List leaves authored by a specific employee, newest first. Same access
 *  rules as `listEmployeeBalances`. */
export function listEmployeeLeaves(id: string, status?: LeaveStatus): Promise<Leave[]> {
  const qs = status ? `?status=${status}` : ''
  return apiRequest<Leave[]>(
    `/api/v1/employees/${encodeURIComponent(id)}/leaves${qs}`,
  )
}
